import { ClockRefresh } from '@timelyapp/icons/solid-icons';
import type { ButtonProps } from '@timelyapp/react';
import { Button } from '@timelyapp/react';
import type { Props as DatePickerProps } from 'components/datepicker/DatePickerDropdown';
import { relativeDateRanges } from 'components/datepicker/utils';
import { DateTime } from 'luxon';
import React from 'react';
import FontAwesome from 'react-fontawesome';
import type { StartOfWeek } from 'types/account';
import { getFirstDayOfWeek, getLastDayOfWeek } from 'utils/date-helper';
import { fromISO, interval } from 'utils/luxon-helpers';
import type { DatePickerMode, DateRange } from './types';

type PickerProps = Pick<
  DatePickerProps,
  | 'startOfWeek'
  | 'baseDate'
  | 'initialMode'
  | 'fullWidth'
  | 'autoPostfixYear'
  | 'disabled'
  | 'dateRange'
  | 'inButtonGroup'
  | 'targetStyle'
  | 'relativeDateRange'
>;
interface Props extends ButtonProps, PickerProps {}

export const getDatePickerTargetText = (
  mode: DatePickerMode,
  baseDate: string,
  options: { autoPostfixYear?: boolean; startOfWeek: StartOfWeek },
  dateRange?: DateRange
) => {
  const base = fromISO(baseDate);
  const now = DateTime.now();
  const shouldAppendYear = !base.hasSame(now, 'year') && options.autoPostfixYear;
  const isToday = +base.startOf('day') === +now.startOf('day');

  let iv: ReturnType<typeof interval> | undefined;
  let format: string | undefined;

  if (dateRange?.from && dateRange?.to) {
    iv = interval(fromISO(dateRange.from).startOf('day'), fromISO(dateRange.to).endOf('day'));
  }

  if (mode === 'week') {
    iv ??= interval(
      getFirstDayOfWeek.core(base, options.startOfWeek),
      getLastDayOfWeek.core(base, options.startOfWeek)
    );
    format = shouldAppendYear ? 'MMM d, yyyy' : 'MMM d';
  } else if (mode === 'month') {
    iv ??= interval(base.startOf('month'), base.endOf('month'));
    format = shouldAppendYear ? 'MMM yyyy' : 'MMMM';
  } else if (mode === 'year') {
    iv ??= interval(base.startOf('year'), base.endOf('year'));
    format = 'yyyy';
  } else {
    iv ??= interval(base.startOf('day'), base.endOf('day'));
    if (/* single day */ iv.hasSame('day')) {
      format = `EEEE, ${shouldAppendYear ? 'MMM d, yyyy' : 'MMMM d'}`;
    } /* custom range */ else {
      format = shouldAppendYear || !iv.hasSame('year') ? 'MMM d, yyyy' : 'MMM d';
    }
  }

  return (mode !== 'week' && iv.hasSame(mode) ? iv.start : iv).toFormat(format, {
    separator: ' → ',
  });
};

const DatePickerTarget: React.FC<Props> = props => {
  const {
    initialMode,
    baseDate,
    autoPostfixYear,
    startOfWeek,
    dateRange,
    inButtonGroup,
    fullWidth,
    targetStyle,
    relativeDateRange,
    ...rest
  } = props;
  const resolvedMode = initialMode === 'custom' ? 'day' : initialMode;

  const style: React.CSSProperties = {};

  if (inButtonGroup) {
    style.borderRadius = 0;
    style.borderLeft = 'unset';
    style.borderRight = 'unset';
    style.marginLeft = 0;
    style.marginRight = 0;
  }

  if (fullWidth) {
    style.flex = '1 1 100%';
  }

  Object.assign(style, targetStyle);

  const text = relativeDateRange
    ? relativeDateRanges.find(r => r.value === relativeDateRange)!.label
    : getDatePickerTargetText(resolvedMode, baseDate, { autoPostfixYear, startOfWeek }, dateRange);

  return (
    <Button style={style} {...rest}>
      {relativeDateRange ? <ClockRefresh /> : <FontAwesome name="calendar" />} <span>{text}</span>
    </Button>
  );
};

export default DatePickerTarget;
